import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../config/axios';
import Navbar from '../components/Navbar';
import CountSubmission from '../components/CountSubmission';
import MyCounts from '../components/MyCounts';
import ChatModal from '../components/ChatModal';
import ChatBell from '../components/ChatBell';
import SupportModal from '../components/SupportModal';
import DashboardHelpVideoModal from '../components/DashboardHelpVideoModal';
import MissingItemTicketModal from '../components/MissingItemTicketModal';
import MissingItemTicketsPanel from '../components/MissingItemTicketsPanel';
import { Sun, Moon, RefreshCw, ClipboardList, Clock, CheckCircle2, PackageMinus, ChevronDown } from 'lucide-react';
import './Dashboard.css';



const SITE_DASH_CACHE = 'si_site_dashboard_v1';

const SiteDashboard = () => {
  const { user, logout } = useAuth();
  const [site, setSite] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [myCounts, setMyCounts] = useState([]);
  const [activeTab, setActiveTab] = useState('submit');
  const [loading, setLoading] = useState(true);
  const [editSiteId, setEditSiteId] = useState(null);
  const [forceReload, setForceReload] = useState(0);
  const [error, setError] = useState('');
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  const [chatConversationId, setChatConversationId] = useState(null);
  const [chatOtherPartyDisplayName, setChatOtherPartyDisplayName] = useState('');
  const [showChatModal, setShowChatModal] = useState(false);
  const [chatRefreshTrigger, setChatRefreshTrigger] = useState(0);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showSitesDashboardHelpVideo, setShowSitesDashboardHelpVideo] = useState(false);
  const [showMissingItemModal, setShowMissingItemModal] = useState(false);
  const [missingItemTickets, setMissingItemTickets] = useState([]);
  const [missingItemLoading, setMissingItemLoading] = useState(true);
  const [missingTicketsExpanded, setMissingTicketsExpanded] = useState(false);
  const [showOutcomeModal, setShowOutcomeModal] = useState(false);
  const [outcomeModalDismissedThisMount, setOutcomeModalDismissedThisMount] = useState(false);
  const [acknowledgingOutcome, setAcknowledgingOutcome] = useState(false);

  const currentParticipant = !user ? null : user.role === 'site' ? { type: 'site', id: user.siteId } : { type: 'user', id: user.id };

  const toggleDark = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('theme', next ? 'dark' : 'light');
  };

  const fetchData = async () => {
    setError('');
    try {
      const [siteRes, inventoryRes, countsRes] = await Promise.all([
        axiosInstance.get('/api/tenant/site/site-info'),
        axiosInstance.get('/api/tenant/site/inventory'),
        axiosInstance.get('/api/tenant/site/my-counts')
      ]);
      const siteData = siteRes.data.site;
      setSite(siteData);
      setInventory(inventoryRes.data.items || []);
      setMyCounts(countsRes.data.batches || []);
      if (siteData) setEditSiteId(siteData.id);
      try {
        sessionStorage.setItem(SITE_DASH_CACHE, JSON.stringify({
          site: siteData,
          inventory: inventoryRes.data.items || [],
          myCounts: countsRes.data.batches || []
        }));
      } catch (_) {}
    } catch (err) {
      console.error('Error fetching data:', err);
      const msg = err.response?.data?.error || err.message || 'Failed to load data';
      setError(err.response?.status === 404 || err.response?.status === 403 ? `Access denied or site not found: ${msg}` : `Error loading dashboard: ${msg}`);
      try {
        const raw = sessionStorage.getItem(SITE_DASH_CACHE);
        if (raw) {
          const j = JSON.parse(raw);
          if (j.site && j.site.id) {
            setSite(j.site);
            setInventory(Array.isArray(j.inventory) ? j.inventory : []);
            if (Array.isArray(j.myCounts)) setMyCounts(j.myCounts);
            setEditSiteId(j.site.id);
          }
        }
      } catch (_) {}
    } finally {
      setLoading(false);
    }
  };

  const loadMissingItemTickets = async () => {
    setMissingItemLoading(true);
    try {
      const res = await axiosInstance.get('/api/tenant/site/missing-item-tickets');
      setMissingItemTickets(res.data.tickets || []);
    } catch (err) {
      console.error('Error fetching missing item tickets:', err);
    } finally {
      setMissingItemLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    loadMissingItemTickets();
  }, []);

  const unseenOutcomeTickets = missingItemTickets.filter((t) => t.outcomeUnseen);

  useEffect(() => {
    if (unseenOutcomeTickets.length > 0 && !outcomeModalDismissedThisMount) {
      setShowOutcomeModal(true);
    }
    if (unseenOutcomeTickets.length === 0) {
      setShowOutcomeModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missingItemTickets.length, unseenOutcomeTickets.length]);

  const handleAcknowledgeOutcome = async (ticketId) => {
    setAcknowledgingOutcome(true);
    try {
      await axiosInstance.put(`/api/tenant/site/missing-item-tickets/${ticketId}/acknowledge-outcome`);
      await loadMissingItemTickets();
    } catch (err) {
      console.error('Error acknowledging missing item outcome:', err);
    } finally {
      setAcknowledgingOutcome(false);
    }
  };

  const handleDismissOutcomeModal = () => {
    setShowOutcomeModal(false);
    setOutcomeModalDismissedThisMount(true);
  };

  const handleOpenOutcomeModal = () => {
    setOutcomeModalDismissedThisMount(false);
    setShowOutcomeModal(true);
  };

  const handleSubmitCount = async (result) => {
    if (result && result.success) {
      await fetchData();
      setActiveTab('my-counts');
      setForceReload(0);
      return { success: true };
    }
    return { success: true };
  };

  const submittedCounts = myCounts.length;
  const pendingCounts = myCounts.filter(c => c.status === 'pending').length;
  const completedCounts = myCounts.filter(c => c.status === 'completed').length;

  const activeCount = myCounts.find(c => c.status === 'pending' || c.status === 'recalled' || c.status === 'rejected');
  const hasCompletedCount = myCounts.some(c => c.status === 'completed');
  const isEditingRecalledCount = editSiteId && activeCount && (activeCount.status === 'recalled' || activeCount.status === 'rejected') && activeCount.siteId === editSiteId;
  const canSubmitNewCount = !hasCompletedCount && (!activeCount || isEditingRecalledCount);

  useEffect(() => {
    if (!loading && !canSubmitNewCount && activeTab === 'submit') setActiveTab('my-counts');
  }, [loading, canSubmitNewCount, activeTab]);

  if (loading) return <div className="sd-loading">Loading...</div>;

  if (!site) {
    return (
      <div className="app-layout">
        <Navbar user={user} onLogout={logout} onOpenSupport={() => setShowSupportModal(true)} onOpenDashboardHelpVideo={() => setShowSitesDashboardHelpVideo(true)} />
        <div className="dashboard site-dashboard">
          <div className="card sd-error-card">
            {error && <div className="sd-error-msg">{error}</div>}
            <p className="sd-error-text">Site information not found. Please contact your administrator.</p>
            <button className="btn sd-btn-retry" onClick={fetchData}>Retry</button>
          </div>
        </div>
        <SupportModal
          open={showSupportModal}
          onClose={() => setShowSupportModal(false)}
          tenantName={typeof window !== 'undefined' ? window.location.hostname : ''}
        />
        <DashboardHelpVideoModal open={showSitesDashboardHelpVideo} onClose={() => setShowSitesDashboardHelpVideo(false)} />
      </div>
    );
  }

  const sites = [site];

  return (
    <div className="app-layout">
      <Navbar user={user} onLogout={logout} onOpenSupport={() => setShowSupportModal(true)} onOpenDashboardHelpVideo={() => setShowSitesDashboardHelpVideo(true)} />
      <div className="dashboard site-dashboard">
        <div className="sd-header">
          <div className="sd-header-left">
            <span className="sd-workspace-badge">{site.name} &middot; TOUCH WORKSPACE</span>
            <h2 className="sd-title">Site Dashboard</h2>
            <p className="sd-subtitle">Fast, touch-friendly inventory capture for {site.name}.</p>
          </div>
          <div className="sd-header-actions">
            <button
              type="button"
              className="sd-action-btn sd-action-btn-missing"
              onClick={() => setShowMissingItemModal(true)}
              title="Report Inventory Issue"
              aria-label="Report Inventory Issue"
            >
              <PackageMinus size={16} aria-hidden />
              <span className="sd-action-btn-label-full">Report Inventory Issue</span>
              <span className="sd-action-btn-label-short">Inventory Issue</span>
            </button>
            <ChatBell
              onSelectConversation={(id, name) => {
                setChatConversationId(id);
                setChatOtherPartyDisplayName(name);
                setShowChatModal(true);
              }}
              buttonClassName="sd-action-btn"
              iconSize={16}
              refreshTrigger={chatRefreshTrigger}
            />
            <button type="button" className="sd-action-btn" onClick={toggleDark} title={dark ? 'Light mode' : 'Dark mode'}>
              {dark ? <Sun size={16} /> : <Moon size={16} />} {dark ? 'Light Mode' : 'Dark Mode'}
            </button>
            <button type="button" className="sd-action-btn" onClick={fetchData} title="Refresh">
              <RefreshCw size={16} /> Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="sd-banner sd-banner-error">
            {error}
            <button type="button" className="btn btn-secondary sd-banner-btn" onClick={fetchData}>Retry</button>
          </div>
        )}

        <div className="sd-stats-row">
          <div className="sd-stat-card sd-stat-indigo">
            <div className="sd-stat-content">
              <span className="sd-stat-label">TOTAL COUNTS</span>
              <span className="sd-stat-value">{submittedCounts}</span>
            </div>
            <ClipboardList size={24} className="sd-stat-icon" />
          </div>
          <div className="sd-stat-card sd-stat-amber">
            <div className="sd-stat-content">
              <span className="sd-stat-label">PENDING REVIEW</span>
              <span className="sd-stat-value">{pendingCounts}</span>
            </div>
            <Clock size={24} className="sd-stat-icon" />
          </div>
          <div className="sd-stat-card sd-stat-emerald">
            <div className="sd-stat-content">
              <span className="sd-stat-label">COMPLETED</span>
              <span className="sd-stat-value">{completedCounts}</span>
            </div>
            <CheckCircle2 size={24} className="sd-stat-icon" />
          </div>
        </div>

        {unseenOutcomeTickets.length > 0 && (
          <button type="button" className="sd-outcome-banner" onClick={handleOpenOutcomeModal}>
            <PackageMinus size={16} aria-hidden />
            The admin team has responded to {unseenOutcomeTickets.length === 1 ? 'an inventory ticket' : `${unseenOutcomeTickets.length} inventory tickets`}. Click to view.
          </button>
        )}

        <div className={'card sd-missing-tickets-section' + (missingTicketsExpanded ? ' sd-missing-tickets-expanded' : '')}>
          <button
            type="button"
            className="sd-missing-tickets-toggle"
            onClick={() => setMissingTicketsExpanded((prev) => !prev)}
            aria-expanded={missingTicketsExpanded}
            aria-controls="sd-missing-tickets-panel"
          >
            <div className="sd-missing-tickets-toggle-main">
              <h3 className="sd-missing-tickets-title">My Inventory Tickets</h3>
              <p className="sd-missing-tickets-summary">
                Open: {missingItemTickets.filter((t) => t.status === 'open').length}
                {' · '}
                Closed: {missingItemTickets.filter((t) => t.status === 'closed').length}
              </p>
            </div>
            <ChevronDown
              size={18}
              className={'sd-missing-tickets-chevron' + (missingTicketsExpanded ? ' sd-missing-tickets-chevron-open' : '')}
              aria-hidden
            />
          </button>
          {missingTicketsExpanded && (
            <div id="sd-missing-tickets-panel" className="sd-missing-tickets-body">
              <MissingItemTicketsPanel
                tickets={missingItemTickets}
                mode="site"
                loading={missingItemLoading}
                onRefresh={loadMissingItemTickets}
                photoBasePath="/api/tenant/site/missing-item-tickets"
              />
            </div>
          )}
        </div>

        <div className="sd-tabs">
          <button type="button" className={'sd-tab' + (activeTab === 'submit' ? ' active' : '') + (!canSubmitNewCount ? ' disabled' : '')} onClick={() => canSubmitNewCount && setActiveTab('submit')} disabled={!canSubmitNewCount}>
            Submit Count
          </button>
          <button type="button" className={'sd-tab' + (activeTab === 'my-counts' ? ' active' : '')} onClick={() => setActiveTab('my-counts')}>
            My Counts
          </button>
        </div>

        {activeTab === 'submit' && canSubmitNewCount && (
            <CountSubmission
            key={`submit-${editSiteId}-${forceReload}`}
            sites={sites}
            inventory={inventory}
            onSubmitCount={handleSubmitCount}
            initialSiteId={editSiteId}
            onSiteSelected={(siteId) => setEditSiteId(null)}
            isSiteLogin={true}
            forceReload={forceReload}
            siteName={site.name}
            onOpenMyCounts={() => setActiveTab('my-counts')}
          />
        )}

        {activeTab === 'submit' && !canSubmitNewCount && (
          <div className="card sd-blocked-card">
            <h3 className="sd-blocked-title">Cannot Submit New Count</h3>
            {hasCompletedCount && !activeCount ? (
              <>
                <p className="sd-blocked-text">Your count has been <strong>reviewed and completed</strong> by your manager.</p>
                <p className="sd-blocked-text">The <strong>"Submit Count"</strong> section will be available again only after an admin starts a <strong>new counting period</strong>.</p>
                <p className="sd-blocked-hint">Until then, you can view your completed count under <strong>"My Counts"</strong>.</p>
              </>
            ) : activeCount?.status === 'pending' ? (
              <>
                <p className="sd-blocked-text">You have a count with status "<strong>Pending Review</strong>" that is awaiting manager action.</p>
                <p className="sd-blocked-text">Please wait until your manager has <strong>completed</strong> or <strong>deleted</strong> the current count before submitting a new one.</p>
              </>
            ) : (
              <>
                <p className="sd-blocked-text">You have a count with status "<strong>{activeCount?.status === 'recalled' ? 'Recalled' : 'Rejected'}</strong>" that needs to be edited and resubmitted.</p>
                <p className="sd-blocked-text">Please go to <strong>"My Counts"</strong> and click <strong>"Edit"</strong> on the {activeCount?.status === 'recalled' ? 'recalled' : 'rejected'} count to resubmit it.</p>
              </>
            )}
            {(activeCount || (hasCompletedCount && !activeCount)) && (
              <div className="sd-blocked-info">
                {activeCount && (
                  <>
                    <p className="sd-blocked-info-line"><strong>Submitted:</strong> {new Date(activeCount.countDate || activeCount.createdAt).toLocaleString()}</p>
                    <p className="sd-blocked-info-line"><strong>Person Counting:</strong> {activeCount.personName || 'N/A'}</p>
                    <p className="sd-blocked-info-line"><strong>Items Counted:</strong> {activeCount.itemCount ?? activeCount.counts?.length ?? 0}</p>
                  </>
                )}
                {hasCompletedCount && !activeCount && (
                  <p className="sd-blocked-info-line"><strong>Status:</strong> Review completed — waiting for new counting period</p>
                )}
              </div>
            )}
            <button type="button" className="btn sd-btn-view-counts" onClick={() => setActiveTab('my-counts')}>View My Counts</button>
          </div>
        )}

        {activeTab === 'my-counts' && (
          <MyCounts
            counts={myCounts}
            onRefresh={fetchData}
            onEditRecalled={(siteId) => {
              setForceReload(prev => prev + 1);
              setEditSiteId(siteId);
              setActiveTab('submit');
            }}
          />
        )}

        {showChatModal && chatConversationId && (
          <ChatModal
            conversationId={chatConversationId}
            otherPartyDisplayName={chatOtherPartyDisplayName}
            onClose={() => {
              setShowChatModal(false);
              setChatConversationId(null);
              setChatOtherPartyDisplayName('');
              setChatRefreshTrigger(t => t + 1);
            }}
            currentParticipant={currentParticipant} />
        )}
        <SupportModal
          open={showSupportModal}
          onClose={() => setShowSupportModal(false)}
          tenantName={typeof window !== 'undefined' ? window.location.hostname : ''}
        />
        <DashboardHelpVideoModal open={showSitesDashboardHelpVideo} onClose={() => setShowSitesDashboardHelpVideo(false)} />
        <MissingItemTicketModal
          open={showMissingItemModal}
          onClose={() => setShowMissingItemModal(false)}
          onSubmitted={loadMissingItemTickets}
        />
        {showOutcomeModal && unseenOutcomeTickets.length > 0 && (
          <div className="me-period-modal-overlay" onClick={handleDismissOutcomeModal} role="dialog" aria-modal="true" aria-labelledby="sd-outcome-modal-title">
            <div className="card me-period-modal" onClick={(e) => e.stopPropagation()}>
              <h3 id="sd-outcome-modal-title" className="me-period-modal-title">Inventory Ticket Update</h3>
              <p className="me-period-modal-desc">
                The admin team has responded to your inventory ticket:
              </p>
              <div className="sd-outcome-modal-ticket">
                <p className="sd-outcome-modal-item"><strong>Item:</strong> {unseenOutcomeTickets[0].itemDescription}</p>
                {unseenOutcomeTickets[0].ticketTypeLabel && (
                  <p className="sd-outcome-modal-item"><strong>Type:</strong> {unseenOutcomeTickets[0].ticketTypeLabel}</p>
                )}
                {unseenOutcomeTickets[0].requestedChange && (
                  <p className="sd-outcome-modal-item"><strong>Requested change:</strong> {unseenOutcomeTickets[0].requestedChange}</p>
                )}
                <p className="sd-outcome-modal-outcome"><strong>Outcome:</strong> {unseenOutcomeTickets[0].adminOutcome}</p>
              </div>
              {unseenOutcomeTickets.length > 1 && (
                <p className="sd-outcome-modal-more">
                  Plus {unseenOutcomeTickets.length - 1} more update{unseenOutcomeTickets.length - 1 === 1 ? '' : 's'} waiting after this one.
                </p>
              )}
              <div className="me-period-modal-actions">
                <button type="button" className="btn btn-secondary" onClick={handleDismissOutcomeModal} disabled={acknowledgingOutcome}>Close</button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleAcknowledgeOutcome(unseenOutcomeTickets[0].id)}
                  disabled={acknowledgingOutcome}
                >
                  {acknowledgingOutcome ? 'Saving…' : 'Mark as seen'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SiteDashboard;